import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CarrierOption, Direction } from "./api";
import { api } from "./api";
import { useBatch, type AddResult, type BatchItem } from "./batch";
import { armAudio, playFeedback } from "./feedback";
import { engineAvailable, startScanning, type StopScanning } from "./scanner";

type Flash = { tone: "ok" | "warn" | "info" | "bad"; titulo: string; detalhe: string };

const FLASH_BG: Record<Flash["tone"], string> = {
  ok: "var(--dep-ok)",
  warn: "var(--dep-warn)",
  info: "var(--dep-info)",
  bad: "var(--dep-danger)",
};

const FLASH_FG: Record<Flash["tone"], string> = {
  ok: "#fff",
  warn: "var(--dep-black)",
  info: "#fff",
  bad: "#fff",
};

function flashFor(r: AddResult): Flash {
  switch (r.kind) {
    case "added":
      return {
        tone: "ok",
        titulo: "Adicionado",
        detalhe: r.item.accountName ?? "escolha a conta na lista",
      };
    case "duplicate-batch":
      return { tone: "bad", titulo: "Já foi bipado", detalhe: "Não foi adicionado de novo" };
    case "already-delivered": {
      const p = r.check.previousScan;
      const quando = p
        ? new Date(p.at).toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "";
      // Vermelho igual ao repetido do lote: os dois significam "não entra", e
      // duas cores para o mesmo desfecho fariam o operador hesitar. O que
      // distingue é a linha de detalhe — aqui ela diz quem entregou e quando.
      // O aviso do Mercado Livre vem primeiro quando existe: ele explica um
      // caso que o histórico daqui não explica — pacote que saiu antes de este
      // sistema começar a contar.
      return {
        tone: "bad",
        titulo: r.check.aviso ? "Este pacote já saiu" : "Já foi bipado",
        detalhe: r.check.aviso ?? (p ? `${p.by ?? "Usuário excluído"}, ${quando}` : "Entregue anteriormente"),
      };
    }
    case "unknown":
      return { tone: "bad", titulo: "Etiqueta não reconhecida", detalhe: "Confira o código" };
  }
}

export function ScanScreen({
  direction,
  reason,
  fotos,
  valorCents,
  titulo,
  onClose,
  onEntregue,
}: {
  direction: Direction;
  /// Motivo da devolução no instante da leitura. Vai congelado em cada item.
  reason: string | null;
  /// Fotos da devolução, já comprimidas. Também congeladas por item.
  fotos?: string[];
  /// Desconto por dano, em centavos, no instante da leitura. Congelado por item
  /// como o motivo — trocar depois não pode reescrever o que já foi bipado.
  valorCents?: number | null;
  titulo: string;
  onClose: () => void;
  /// Chamado depois de Entregar, com o que foi registrado. A tela de Devolução
  /// usa para montar o histórico e limpar o formulário.
  onEntregue?: (
    itens: Array<{
      code: string;
      motivo: string;
      custoCents: number | null;
      fotos: number;
    }>
  ) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const batch = useBatch();
  const [erro, setErro] = useState<string | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);
  const [aberto, setAberto] = useState(false);
  const [entregando, setEntregando] = useState(false);
  const [carriers, setCarriers] = useState<CarrierOption[]>([]);
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState("");

  /// Pacote recém-lido cuja conta a etiqueta não resolveu — Shopee e Loja
  /// Integrada. Fica aqui para a escolha acontecer na própria tela da câmera,
  /// em vez de exigir abrir a gaveta e procurar o item.
  const [pendente, setPendente] = useState<BatchItem | null>(null);

  // Enquanto a escolha de conta está aberta, novas leituras são ignoradas: a
  // câmera continua apontada para a bancada e pegaria a etiqueta seguinte antes
  // de o operador ter respondido a esta.
  const ocupado = useRef(false);
  const addRef = useRef(batch.add);
  addRef.current = batch.add;
  const reasonRef = useRef(reason);
  reasonRef.current = reason;
  const fotosRef = useRef(fotos ?? []);
  fotosRef.current = fotos ?? [];
  const valorRef = useRef(valorCents ?? null);
  valorRef.current = valorCents ?? null;
  const pendenteRef = useRef(pendente);
  pendenteRef.current = pendente;

  useEffect(() => {
    void api.carriers().then(setCarriers).catch(() => undefined);
  }, []);

  const processar = useCallback(
    async (code: string) => {
      if (ocupado.current || pendenteRef.current) return;
      ocupado.current = true;

      try {
          const r = await addRef.current(
          code,
          direction,
          reasonRef.current,
          fotosRef.current,
          valorRef.current
        );
        playFeedback(
          r.kind === "added" ? "OK" : r.kind === "unknown" ? "UNIDENTIFIED" : "DUPLICATE"
        );

        // Conta indefinida: em vez do retorno passageiro, abre a escolha na
        // hora. São no máximo duas opções, e resolver agora evita acumular
        // pendências para o fim do lote.
        if (r.kind === "added" && !r.item.accountId && r.item.check.candidates.length > 0) {
          setPendente(r.item);
          return;
        }

        setFlash(flashFor(r));
        setTimeout(() => setFlash(null), 1600);
      } catch (err) {
        setErro(err instanceof Error ? err.message : "Falha ao conferir o código.");
      } finally {
        ocupado.current = false;
      }
    },
    [direction]
  );

  useEffect(() => {
    let stop: StopScanning | undefined;
    let cancelado = false;

    void (async () => {
      const video = videoRef.current;
      if (!video) return;
      const fn = await startScanning(video, (code) => void processar(code), setErro);
      if (cancelado) fn();
      else stop = fn;
    })();

    return () => {
      cancelado = true;
      stop?.();
    };
  }, [processar]);

  const entregar = async () => {
    setEntregando(true);
    setErro(null);

    // Capturado ANTES do envio: `deliver` esvazia o lote, e depois não haveria
    // mais de onde tirar motivo, custo e foto de cada item.
    const antesDoEnvio = batch.items.map((i) => ({
      code: i.check.code,
      motivo: i.reason ?? "sem motivo",
      custoCents: i.returnCostCents,
      fotos: i.photoDataUrls.length,
    }));

    try {
      const r = await batch.deliver();

      const gravados = new Set(r.registrados.map((x) => x.code));
      onEntregue?.(antesDoEnvio.filter((x) => gravados.has(x.code)));

      if (r.falhas.length > 0) {
        setErro(`${r.falhas.length} não entraram: ${r.falhas[0]?.message ?? ""}`);
      } else {
        onClose();
      }
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Falha ao entregar.");
    } finally {
      setEntregando(false);
    }
  };

  const enviarDigitado = async () => {
    const code = typed.trim();
    if (!code) return;
    setTyped("");
    setTyping(false);
    await processar(code);
  };

  const total = batch.items.length;

  // Direto no <body>, e não onde o componente foi chamado.
  //
  // Este componente é usado dentro de telas que rolam (`<main>` tem
  // `overflow-y: auto`). No Safari do iPhone, um `position: fixed` preso
  // dentro de um contêiner que rola fica CORTADO pelos limites daquele
  // contêiner em vez de cobrir a tela toda — o menu de baixo aparecia por
  // cima da câmera. No Android isso não acontecia, e só apareceu quando
  // alguém mandou um print do iPhone. O portal tira a câmera da árvore de
  // onde ela foi chamada e bota direto no `<body>`, imune a qualquer
  // aninhamento — o mesmo defeito não pode voltar por engano numa tela nova.
  return createPortal(
    <div className="scan-screen">
      <video ref={videoRef} muted playsInline />

      {/* Visor com cantos, como no exemplo: mostra onde encostar a etiqueta. */}
      <div className="viewfinder" aria-hidden="true">
        <span className="c tl" />
        <span className="c tr" />
        <span className="c bl" />
        <span className="c br" />
      </div>

      <button className="scan-back" onClick={onClose} aria-label="Voltar">
        ←
      </button>

      <div className="scan-hint">{titulo}</div>

      {engineAvailable() === "jsqr" && (
        <div className="scan-warn">
          Este aparelho lê só QR. A etiqueta da Shopee é código de barras — use “Digitar
          código”.
        </div>
      )}

      {flash && (
        <div
          className="flash"
          style={{ background: FLASH_BG[flash.tone], color: FLASH_FG[flash.tone] }}
          role="status"
          aria-live="assertive"
        >
          <div className="label">{flash.titulo}</div>
          <div className="detail">{flash.detalhe}</div>
        </div>
      )}

      {/* Escolha de conta na própria tela da câmera.
          A etiqueta da Shopee e a da Loja Integrada não dizem de qual loja são,
          e com os pacotes misturados na bancada não há como adivinhar. Trazer
          a escolha para cá troca "abrir a gaveta, achar o item, tocar" por um
          toque só, sem tirar os olhos da bancada. */}
      {pendente && (
        <div className="conta-pick" role="dialog" aria-label="Escolha a conta">
          <div className="titulo">De qual conta?</div>
          <code>{pendente.check.code}</code>

          <div className="opcoes">
            {pendente.check.candidates.map((c) => (
              <button
                key={c.id}
                style={{ borderColor: c.color ?? undefined }}
                onClick={() => {
                  batch.setAccount(pendente.key, c.id, c.name);
                  setPendente(null);
                  playFeedback("OK");
                }}
              >
                <span className="ponto" style={{ background: c.color ?? "var(--dep-border)" }} />
                {c.name}
              </button>
            ))}
          </div>

          <button
            className="depois"
            onClick={() => setPendente(null)}
          >
            Decidir depois
          </button>
        </div>
      )}

      {/* Painel deslizante: recolhido mostra a contagem; arrastado para cima
          mostra o lote inteiro, com excluir e trocar transportadora. */}
      <div className={`sheet${aberto ? " open" : ""}`}>
        <button
          className="grabber"
          onClick={() => setAberto((v) => !v)}
          aria-expanded={aberto}
          aria-label={aberto ? "Recolher lista" : "Ver os pacotes bipados"}
        >
          <span />
        </button>

        {!aberto ? (
          <div className="sheet-head">
            <div className="count">
              {total} {total === 1 ? "pacote" : "pacotes"}
            </div>
            <div className="sub">
              {total === 0
                ? "Aponte a câmera para a etiqueta."
                : "Deslize para cima para ver e corrigir."}
            </div>
          </div>
        ) : (
          <div className="sheet-list">
            <div className="sheet-head compact">
              <div className="count">
                {total} {total === 1 ? "pacote" : "pacotes"}
              </div>
            </div>

            {total === 0 ? (
              <p className="sheet-empty">Nada bipado ainda.</p>
            ) : (
              batch.items.map((item) => (
                <ItemLinha
                  key={item.key}
                  item={item}
                  carriers={carriers}
                  onRemove={() => batch.remove(item.key)}
                  onCarrier={(c) => batch.setCarrier(item.key, c.id, c.name)}
                  onAccount={(id, nome) => batch.setAccount(item.key, id, nome)}
                  onNotes={(texto) => batch.setNotes(item.key, texto)}
                  onReturnCost={(c) => batch.setReturnCost(item.key, c)}
                />
              ))
            )}
          </div>
        )}

        {erro && <div className="sheet-erro">{erro}</div>}

        <div className="sheet-actions">
          <button
            className="btn-ghost"
            onClick={() => {
              armAudio();
              setTyping(true);
            }}
          >
            Digitar
          </button>
          <button
            className="btn-deliver"
            disabled={total === 0 || entregando}
            onClick={() => void entregar()}
          >
            {entregando ? "Entregando…" : `Entregar ${total > 0 ? total : ""}`}
          </button>
        </div>
      </div>

      {typing && (
        <div className="typing">
          <input
            autoFocus
            value={typed}
            placeholder="Código da etiqueta"
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void enviarDigitado()}
          />
          <button className="btn-deliver" onClick={() => void enviarDigitado()}>
            Conferir
          </button>
          <button className="btn-ghost" onClick={() => setTyping(false)}>
            Cancelar
          </button>
        </div>
      )}
    </div>,
    document.body
  );
}

function ItemLinha({
  item,
  carriers,
  onRemove,
  onCarrier,
  onAccount,
  onNotes,
  onReturnCost,
}: {
  item: BatchItem;
  carriers: CarrierOption[];
  onRemove: () => void;
  onCarrier: (c: CarrierOption) => void;
  onAccount: (id: string, nome: string) => void;
  onNotes: (texto: string) => void;
  onReturnCost: (centavos: number | null) => void;
}) {
  // O pedido sincronizado vem primeiro quando existe; senão, o que a
  // plataforma contou na hora da leitura. Hoje só o Mercado Livre Flex
  // responde — nas outras etiquetas a linha simplesmente não aparece, que é
  // melhor que mostrar "produtos: —" em toda bipagem.
  const produtos = item.check.order?.items ?? item.check.produtos ?? [];

  return (
    <div className="sheet-item">
      <div className="top">
        <code>{item.check.code}</code>
        <button className="remove" onClick={onRemove} aria-label="Remover do lote">
          Excluir
        </button>
      </div>

      <div className="meta">
        {item.check.platform?.name ?? "—"}
        {item.accountName ? ` · ${item.accountName}` : " · conta a definir"}
        {item.check.order && ` · pedido ${item.check.order.externalId}`}
      </div>

      {produtos.length > 0 && (
        <div className="meta">{produtos.map((p) => `${p.quantity}× ${p.title}`).join(", ")}</div>
      )}

      {item.reason && <div className="meta">Motivo: {item.reason}</div>}

      {!item.accountId && item.check.candidates.length > 0 && (
        <div className="choices">
          {item.check.candidates.map((c) => (
            <button key={c.id} onClick={() => onAccount(c.id, c.name)}>
              {c.name}
            </button>
          ))}
        </div>
      )}

      <label className="carrier-pick">
        Transportadora
        <select
          value={item.carrierId ?? ""}
          onChange={(e) => {
            const c = carriers.find((x) => x.id === e.target.value);
            if (c) onCarrier(c);
          }}
        >
          <option value="">—</option>
          {carriers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>

      {/* Observação por pacote. Fica no item, não no lote: cada pacote tem a
          sua história, e uma nota do lote inteiro não diria de qual foi. */}
      <input
        className="obs-input"
        placeholder="Observação (opcional)"
        maxLength={500}
        value={item.notes}
        onChange={(e) => onNotes(e.target.value)}
      />

      {/* Custo só faz sentido na devolução: o pacote sai do que se paga pela
          saída, mas pode gerar cobrança se voltar danificado. */}
      {item.direction === "RETURN" && (
        <label className="carrier-pick">
          Custo R$
          <input
            className="obs-input"
            inputMode="decimal"
            placeholder="0,00"
            value={
              item.returnCostCents === null
                ? ""
                : (item.returnCostCents / 100).toFixed(2).replace(".", ",")
            }
            onChange={(e) => {
              const texto = e.target.value.trim().replace(",", ".");
              if (texto === "") return onReturnCost(null);
              if (!/^\d+(\.\d{0,2})?$/.test(texto)) return;

              // Centavos a partir do texto, nunca por Number(v) * 100: em ponto
              // flutuante 11.99 * 100 dá 1198.9999… e truncar perderia centavo.
              const [reais = "0", cents = ""] = texto.split(".");
              onReturnCost(Number(reais) * 100 + Number(cents.padEnd(2, "0")));
            }}
          />
        </label>
      )}
    </div>
  );
}
